import { MtFileType } from "gen/clients/llts";

/**
 * Mapping of MtFileType enum values to their corresponding MIME types and file extensions.
 * This is used to determine which file types are accepted in file upload components.
 */
export const MT_FILE_TYPE_TO_ACCEPTED_FILES: Record<MtFileType, string[]> = {
  [MtFileType.TEXT]: ["text/plain", ".txt"],
  [MtFileType.DELIMITED_TEXT]: [
    "text/csv", // .csv
    "text/tab-separated-values", // .tsv
    ".csv",
    ".tsv"
  ],
  [MtFileType.MARKUP]: [
    "text/html", // .html, .htm
    ".html",
    ".htm"
  ],
  [MtFileType.LOCALIZATION]: [
    "application/xliff+xml", // .xlf, .xliff, .mqxliff, .sdlxliff
    ".xlf",
    ".xliff",
    ".mqxliff",
    ".sdlxliff"
  ],
  [MtFileType.WORD]: [
    "application/msword", // .doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    ".doc",
    ".docx"
  ],
  [MtFileType.EXCEL]: [
    "application/vnd.ms-excel", // .xls
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    ".xls",
    ".xlsx"
  ],
  [MtFileType.POWERPOINT]: [
    "application/vnd.ms-powerpoint", // .ppt
    "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
    ".ppt",
    ".pptx"
  ],
  [MtFileType.OPEN_DOCUMENT]: [
    "application/vnd.oasis.opendocument.text", // .odt
    "application/vnd.oasis.opendocument.spreadsheet", // .ods
    "application/vnd.oasis.opendocument.presentation", // .odp
    ".odt",
    ".ods",
    ".odp"
  ],
  [MtFileType.RICH_TEXT]: ["application/rtf", ".rtf"],
  [MtFileType.PDF]: ["application/pdf", ".pdf"],
  [MtFileType.IMAGE]: [
    "image/png", // .png
    "image/jpeg", // .jpg, .jpeg
    "image/gif", // .gif
    "image/svg+xml", // .svg
    "image/tiff", // .tiff
    "image/bmp", // .bmp
    "image/webp", // .webp
    ".png",
    ".jpg",
    ".jpeg",
    ".gif",
    ".svg",
    ".tiff",
    ".bmp",
    ".webp"
  ],
  [MtFileType.EMAIL]: ["application/vnd.ms-outlook", ".msg"]
};

export const MT_FILE_TYPE_TO_NAME: Record<MtFileType, string> = {
  [MtFileType.TEXT]: "Text",
  [MtFileType.DELIMITED_TEXT]: "Delimited Text",
  [MtFileType.MARKUP]: "Markup",
  [MtFileType.LOCALIZATION]: "Localization",
  [MtFileType.WORD]: "Word",
  [MtFileType.EXCEL]: "Excel",
  [MtFileType.POWERPOINT]: "PowerPoint",
  [MtFileType.OPEN_DOCUMENT]: "Open Document",
  [MtFileType.RICH_TEXT]: "Rich Text",
  [MtFileType.PDF]: "PDF",
  [MtFileType.IMAGE]: "Image",
  [MtFileType.EMAIL]: "Email"
};

const ALL_SUPPORTED_MT_FILE_TYPES = Object.values(MtFileType) as MtFileType[];

/**
 * All supported file types for machine translation file uploads.
 */
export const ALL_SUPPORTED_DOCUMENT_FILE_TYPES = Array.from(
  new Set(ALL_SUPPORTED_MT_FILE_TYPES.flatMap(fileType => MT_FILE_TYPE_TO_ACCEPTED_FILES[fileType]))
);

export const ALL_SUPPORTED_DOCUMENT_FILE_TYPE_NAMES = ALL_SUPPORTED_MT_FILE_TYPES.map(
  fileType => MT_FILE_TYPE_TO_NAME[fileType]
);

/**
 * Converts an array of MtFileType values to an array of accepted file types (MIME types and extensions)
 * that can be used with file upload components.
 *
 * @param allowedFileTypes - Array of MtFileType values, or undefined
 * @returns Array of accepted file types (MIME types and extensions)
 */
export function getAcceptedFileTypes(allowedFileTypes?: MtFileType[]): string[] {
  // If allowedFileTypes is undefined, allow all supported document types
  if (!allowedFileTypes) {
    return ALL_SUPPORTED_DOCUMENT_FILE_TYPES;
  }

  // If allowedFileTypes is an empty array, return empty array (no files allowed)
  if (allowedFileTypes.length === 0) {
    return [];
  }

  // Map each allowed file type to its accepted MIME types and extensions
  const acceptedFiles = allowedFileTypes.flatMap(fileType => MT_FILE_TYPE_TO_ACCEPTED_FILES[fileType] || []);

  // Remove duplicates and return
  return Array.from(new Set(acceptedFiles));
}

/**
 * Gets a human-readable list of allowed file type names for display purposes.
 *
 * @param allowedFileTypes - Array of MtFileType values, or undefined
 * @returns Array of human-readable file type names
 */
export function getAllowedFileTypeNames(allowedFileTypes?: MtFileType[]): string[] {
  if (!allowedFileTypes) {
    return ALL_SUPPORTED_DOCUMENT_FILE_TYPE_NAMES;
  }

  if (allowedFileTypes.length === 0) {
    return [];
  }

  return Array.from(new Set(allowedFileTypes.map(fileType => MT_FILE_TYPE_TO_NAME[fileType])));
}
